import {
  IonButtons,
  IonModal,
  IonTitle,
  IonToolbar,
  IonHeader,
  IonContent,
  IonIcon,
  IonText,
  IonBadge,
} from '@ionic/react';
import HStack from 'components/HStack';
import DynamicButton from 'components/buttons/DynamicButton';
import AnimatedPodium from 'components/shared/AnimatedPodium';
import {
  calendarOutline,
  trophy,
  medal,
  locationOutline,
  flagOutline,
  playOutline,
} from 'ionicons/icons';
import {
  ComponentProps,
  FunctionComponent,
  useRef,
  useEffect,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ExploreElement } from '../../models/ExploreElementModel/ExploreElementModel';
import { useStores } from '../../models';
import { useCloudStorage } from 'services/Database/CloudStorageService';
import './ContestModal.css';
import { logger } from 'services/Logger';

export interface ContestModalProps extends ComponentProps<typeof IonModal> {
  isOpen: boolean;
  onDidDismiss: () => void;
  contest: ExploreElement | null;
  onEditElement?: (element: ExploreElement) => void;
  onDeleteElement?: (element: ExploreElement) => void;
  onPersonClick?: (personName: string) => void;
}

const ContestModal: FunctionComponent<ContestModalProps> = ({
  isOpen,
  onDidDismiss,
  contest,
  onEditElement,
  onDeleteElement,
  onPersonClick,
  ...rest
}) => {
  const { t } = useTranslation();
  const modal = useRef<HTMLIonModalElement>(null);
  const { currentUser } = useStores();
  const cloudStorage = useCloudStorage();
  const [updateCounter, setUpdateCounter] = useState(0);

  // Real-time contest data refresh
  const [contestData, setContestData] = useState(contest);

  // Update contest data when contest changes
  useEffect(() => {
    setContestData(contest);
  }, [contest, updateCounter]);

  // Listen for contest updates
  useEffect(() => {
    if (!isOpen || !contest) return;

    const handleContestUpdate = () => {
      setUpdateCounter((prev) => prev + 1);
      setContestData(contest); // KEIN Spread mehr!
    };

    // Listen for global contest updates
    const originalUpdate = window.contestSelectionUpdate;
    window.contestSelectionUpdate = (contestId, runCount, maxRuns) => {
      if (contestId === contest.id) {
        handleContestUpdate();
      }
      if (originalUpdate) {
        originalUpdate(contestId, runCount, maxRuns);
      }
    };

    return () => {
      window.contestSelectionUpdate = originalUpdate;
    };
  }, [isOpen, contest]);

  if (!contestData) return null;

  // Helper: Wandelt Wert in Date-Objekt um, falls nötig
  function parseDateOrDefault(value: any, fallback: Date = new Date()): Date {
    if (!value) return fallback;
    if (value instanceof Date && !isNaN(value.getTime())) return value;
    if (typeof value === 'string') {
      const d = new Date(value);
      if (!isNaN(d.getTime())) return d;
    }
    if (typeof value === 'object' && value.seconds !== undefined) {
      return new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1e6);
    }
    return fallback;
  }

  // Contest-specific data
  let startDate = parseDateOrDefault(contestData.startDate);
  let endDate = parseDateOrDefault(contestData.endDate);

  if (endDate < startDate) {
    const tmp = endDate;
    endDate = startDate;
    startDate = tmp;
  }

  const maxRuns = contestData.maxRuns || 3;
  const contestRules = contestData.contestRules || 'No rules specified';

  const contestParticipants = contestData.getTopParticipants(10);
  const isUserParticipating = currentUser?.uid
    ? contestData.isUserParticipating(currentUser.uid)
    : false;
  const participantCount = contestData.getParticipantCount();
  const isContestFull = contestData.isContestFull();

  const getPositionIcon = (position: number) => {
    switch (position) {
      case 0:
        return <span className="position-number">0</span>;
      case 1:
        return <IonIcon icon={trophy} className="position-icon gold" />;
      case 2:
        return <IonIcon icon={medal} className="position-icon silver" />;
      case 3:
        return <IonIcon icon={medal} className="position-icon bronze" />;
      default:
        return <span className="position-number">{position}</span>;
    }
  };

  const formatDateTime = (date: Date) => {
    return `${date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })} ${date.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  };

  const getContestStatus = () => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (now < start) {
      return {
        status: 'upcoming',
        color: 'warning',
        text: <Trans i18nKey="CONTEST_STATUS_UPCOMING">Bevorstehend</Trans>,
      };
    } else if (now >= start && now <= end) {
      return {
        status: 'active',
        color: 'success',
        text: <Trans i18nKey="CONTEST_STATUS_ACTIVE">Aktiv</Trans>,
      };
    } else {
      return {
        status: 'finished',
        color: 'medium',
        text: <Trans i18nKey="CONTEST_STATUS_FINISHED">Beendet</Trans>,
      };
    }
  };

  const contestStatus = getContestStatus();

  const handleJoinContest = async () => {
    if (!currentUser) return;
    const success = contestData.joinContest(
      currentUser.uid,
      currentUser.displayName || 'Anonymous',
      '',
      currentUser.photoURL || ''
    );
    if (success) {
      setContestData(contestData); // KEIN Spread mehr!
      try {
        await cloudStorage.updateExploreElement(
          contestData.toServer(contestData.id)
        );
        setUpdateCounter((c) => c + 1);
      } catch (err) {
        logger.error('Firestore update failed:', err);
      }
    }
  };

  const handleLeaveContest = async () => {
    if (!currentUser) return;
    const success = contestData.leaveContest(currentUser.uid);
    if (success) {
      setContestData(contestData); // KEIN Spread mehr!
      try {
        await cloudStorage.updateExploreElement(
          contestData.toServer(contestData.id)
        );
        setUpdateCounter((c) => c + 1);
      } catch (err) {
        logger.error('Firestore update failed:', err);
      }
    }
  };

  const isJoinDisabled = contestStatus.status === 'finished' || isContestFull;
  const isLeaveDisabled = contestStatus.status === 'finished';

  const getUserContestStats = () => {
    if (!currentUser?.uid) return null;

    const rankings = (contestData as any).rankings || [];
    const userEntry = rankings.find(
      (entry: any) => entry.userId === currentUser.uid
    );

    return userEntry
      ? {
          runCount: userEntry.runCount || 0,
          bestScore: userEntry.bestScore || 0,
          scores: userEntry.scores || [],
          position: userEntry.position || null,
        }
      : null;
  };

  const userStats = getUserContestStats();

  // Extrahiere die Top 3 Namen aus den Contest-Teilnehmern (nur mit Punkten > 0)
  const getTopThreeWinners = () => {
    const participantsWithScores = contestParticipants.filter(
      (p) => p.bestScore > 0
    );
    const topThree = participantsWithScores.slice(0, 3);

    return {
      first: topThree[0]?.userName || 'Platz 1',
      second: topThree[1]?.userName || 'Platz 2',
      third: topThree[2]?.userName || 'Platz 3',
    };
  };

  // Extrahiere die Top 3 Scores aus den Contest-Teilnehmern (nur mit Punkten > 0)
  const getTopThreeScores = () => {
    const participantsWithScores = contestParticipants.filter(
      (p) => p.bestScore > 0
    );
    const topThree = participantsWithScores.slice(0, 3);

    return {
      first: topThree[0]?.bestScore || 0,
      second: topThree[1]?.bestScore || 0,
      third: topThree[2]?.bestScore || 0,
    };
  };
  return (
    <IonModal
      ref={modal}
      isOpen={isOpen}
      onWillDismiss={onDidDismiss}
      className="edit-modal"
      presentingElement={undefined}
      {...rest}
    >
      <span style={{ display: 'none' }}>{updateCounter}</span>
      <IonHeader className="ion-no-border">
        {/* <div className="modal-image-svg">
          <img src={'/assets/images/Pic_Continent_Filter.svg'} alt="Header" />
        </div> */}
        <IonToolbar className="edit-modal-toptoolbar">
          <IonButtons slot="start">
            {(contestStatus.status === 'active' ||
              contestStatus.status === 'upcoming') &&
              currentUser && (
                <>
                  <DynamicButton
                    isToggleable
                    text={
                      isContestFull
                        ? 'Contest voll'
                        : isUserParticipating
                          ? 'Verlassen'
                          : 'Beitreten'
                    }
                    iconName="LeaveDownIcon"
                    iconNameToggle="JoinUpIcon"
                    buttonType="icon-full"
                    size="large"
                    fill="clear"
                    className="spnx-button"
                    disabled={!isUserParticipating ? isJoinDisabled : false}
                    toggled={isUserParticipating}
                    onClick={
                      !isUserParticipating
                        ? handleJoinContest
                        : handleLeaveContest
                    }
                  />
                </>
              )}
            {contestData.isOwnedByUser(currentUser?.uid) && (
              <>
                <DynamicButton
                  text={<Trans i18nKey="EDIT_CONTEST" />}
                  iconName="AddUpIcon"
                  buttonType="icon-full"
                  size="large"
                  fill="clear"
                  className="spnx-button"
                  onClick={() => onEditElement?.(contestData)}
                />
                <DynamicButton
                  text={<Trans i18nKey="DELETE_CONTEST" />}
                  iconName="DeleteUpIcon"
                  buttonType="icon-full"
                  size="large"
                  fill="clear"
                  className="spnx-button"
                  onClick={() => onDeleteElement?.(contestData)}
                />
              </>
            )}
          </IonButtons>
          <IonButtons slot="end">
            <DynamicButton
              text={<Trans i18nKey="CANCEL" />}
              iconName="CancelUpIcon"
              buttonType="icon-full"
              size="xlarge"
              fill="clear"
              className="spnx-button"
              onClick={onDidDismiss}
            />
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent
        style={{ '--background': 'transparent', '--border-colour': 0 }}
      >
        <div className="contest-modal-wrapper">
          <div className="contest-modal-header">
            <h1>{contestData.name}</h1>
            <IonBadge
              color={contestStatus.color}
              className="contest-status-badge"
            >
              {contestStatus.text}
            </IonBadge>
          </div>

          {isUserParticipating && userStats && (
            <div className="contest-modal-description user-stats-section">
              <h2>
                <Trans i18nKey="YOUR_PERFORMANCE">🎯 Your Performance</Trans>
              </h2>
              <div className="user-stats-grid">
                <div className="stat-item">
                  <div className="stat-label">
                    <Trans i18nKey="RUNS">Runs</Trans>
                  </div>
                  <div className="stat-value">
                    {userStats.runCount}/{maxRuns}
                  </div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">
                    <Trans i18nKey="BEST_SCORE">Best Score</Trans>
                  </div>
                  <div className="stat-value">
                    {userStats.bestScore.toFixed(1)}
                  </div>
                </div>
                {userStats.position && (
                  <div className="stat-item">
                    <div className="stat-label">
                      <Trans i18nKey="POSITION">Position</Trans>
                    </div>
                    <div className="stat-value">#{userStats.position}</div>
                  </div>
                )}
              </div>

              {userStats.scores.length > 0 && (
                <div className="recent-scores">
                  <h3>Recent Scores</h3>
                  <div className="scores-list">
                    {userStats.scores
                      .slice(-5)
                      .reverse()
                      .map((score: number, index: number) => (
                        <span
                          key={index}
                          className={`score-badge ${
                            score === userStats.bestScore ? 'best-score' : ''
                          }`}
                        >
                          {score.toFixed(1)}
                          {score === userStats.bestScore && ' 👑'}
                        </span>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {contestData.description && (
            <div className="contest-modal-description">
              <h2>
                <Trans i18nKey="DESCRIPTION">Description</Trans>
              </h2>
              <p>{contestData.description}</p>
            </div>
          )}

          <div className="contest-modal-details">
            <HStack className="contest-detail-item">
              <IonIcon icon={calendarOutline} style={{ color: 'white' }} />
              <IonText>
                {formatDateTime(startDate)} - {formatDateTime(endDate)}
              </IonText>
            </HStack>

            {contestData.location && (
              <HStack className="contest-detail-item">
                <IonIcon icon={locationOutline} style={{ color: 'white' }} />
                <IonText>{contestData.location}</IonText>
              </HStack>
            )}

            {contestData.country && (
              <HStack className="contest-detail-item">
                <IonIcon icon={flagOutline} style={{ color: 'white' }} />
                <IonText>{contestData.country}</IonText>
              </HStack>
            )}

            <HStack className="contest-detail-item">
              <IonIcon icon={playOutline} style={{ color: 'white' }} />
              <IonText>
                <Trans i18nKey="MAX_RUNS_PER_PARTICIPANT">
                  Max. {{ maxRuns }} Runs pro Teilnehmer
                </Trans>
              </IonText>
            </HStack>
          </div>

          {contestRules && (
            <div className="contest-modal-description">
              <h2>
                <Trans i18nKey="RULES">Regeln</Trans>
              </h2>
              <p>{contestRules}</p>
            </div>
          )}

          {contestParticipants.length > 0 && (
            <div className="contest-modal-description">
              <h2>🏆 Current Rankings ({participantCount} Participants)</h2>
              {/* AnimatedPodium für Top 3 Gewinner - nur wenn mindestens ein Teilnehmer Punkte hat */}
              {contestParticipants.some(
                (participant) => participant.bestScore > 0
              ) && (
                <div className="contest-modal-podium">
                  <AnimatedPodium
                    winners={getTopThreeWinners()}
                    scores={getTopThreeScores()}
                    onPersonClick={onPersonClick}
                  />
                </div>
              )}{' '}
              <div className="ranking-entries">
                {(() => {
                  const participantsWithScores = contestParticipants.filter(
                    (p) => p.bestScore > 0
                  );
                  const participantsWithoutScores = contestParticipants.filter(
                    (p) => p.bestScore === 0
                  );
                  const hasPodium = participantsWithScores.length > 0;

                  if (hasPodium) {
                    // Zeige Teilnehmer mit Punkten ab Platz 4 + alle mit 0 Punkten
                    const scorersAfterPodium = participantsWithScores.slice(3); // Ab 4. Platz
                    const allLeaderboardParticipants = [
                      ...scorersAfterPodium,
                      ...participantsWithoutScores,
                    ];

                    return allLeaderboardParticipants.map(
                      (participant, index) => {
                        let position;
                        if (participant.bestScore === 0) {
                          position = 0; // Alle mit 0 Punkten bekommen Position 0
                        } else {
                          // Berechne die echte Position für Scorer (4 + Index in der Scorer-Liste)
                          const scorerIndex = scorersAfterPodium.findIndex(
                            (p) => p.userId === participant.userId
                          );
                          position = 4 + scorerIndex; // Beginnt bei Platz 4
                        }
                        const isCurrentUser =
                          participant.userId === currentUser?.uid;

                        return (
                          <div
                            key={`${participant.userId}-${updateCounter}`}
                            className={`ranking-modal-item ${
                              isCurrentUser ? 'current-user' : ''
                            }`}
                            onClick={() =>
                              onPersonClick &&
                              onPersonClick(participant.userName)
                            }
                            style={{
                              cursor: onPersonClick ? 'pointer' : 'default',
                            }}
                          >
                            <div className="ranking-position">
                              {getPositionIcon(position)}
                            </div>
                            <div className="ranking-info">
                              <div className="participant-name">
                                {participant.userName}
                                {isCurrentUser && ' (You)'}
                              </div>
                              <div className="participant-runs">
                                Runs: {participant.runCount || 0}/{maxRuns}
                              </div>
                            </div>
                            <div className="participant-score">
                              {participant.bestScore.toFixed(1)}
                            </div>
                            {participant.nationality && (
                              <IonIcon
                                icon={flagOutline}
                                className="nationality-flag"
                                title={participant.nationality}
                              />
                            )}
                          </div>
                        );
                      }
                    );
                  } else {
                    // Zeige alle Teilnehmer wenn kein Podium
                    return contestParticipants.map((participant, index) => {
                      const position = 0; // Alle bekommen Position 0 wenn niemand Punkte hat
                      const isCurrentUser =
                        participant.userId === currentUser?.uid;

                      return (
                        <div
                          key={`${participant.userId}-${updateCounter}`}
                          className={`ranking-modal-item ${
                            isCurrentUser ? 'current-user' : ''
                          }`}
                          onClick={() =>
                            onPersonClick && onPersonClick(participant.userName)
                          }
                          style={{
                            cursor: onPersonClick ? 'pointer' : 'default',
                          }}
                        >
                          <div className="ranking-position">
                            {getPositionIcon(position)}
                          </div>
                          <div className="ranking-info">
                            <div className="participant-name">
                              {participant.userName}
                              {isCurrentUser && ' (You)'}
                            </div>
                            <div className="participant-runs">
                              Runs: {participant.runCount || 0}/{maxRuns}
                            </div>
                          </div>
                          <div className="participant-score">
                            {participant.bestScore.toFixed(1)}
                          </div>
                          {participant.nationality && (
                            <IonIcon
                              icon={flagOutline}
                              className="nationality-flag"
                              title={participant.nationality}
                            />
                          )}
                        </div>
                      );
                    });
                  }
                })()}
              </div>
            </div>
          )}
        </div>
      </IonContent>
    </IonModal>
  );
};

export default ContestModal;
